import React, { createContext, useState, useEffect, ReactNode } from "react";
import { translations, SupportedLanguage } from "./translations";
import { TranslationKeys } from "./translations/en";

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: TranslationKeys;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

interface LanguageProviderProps {
  children: ReactNode;
}

// Helper function to detect device/browser language
const getDeviceLanguage = (): SupportedLanguage => {
  // Check navigator.languages array first (more accurate, includes all preferred languages)
  const languages = navigator.languages || [navigator.language];

  for (const lang of languages) {
    const langCode = lang.split("-")[0].toLowerCase();
    if (langCode === "fr") return "fr";
    if (langCode === "es") return "es";
    if (langCode === "en") return "en";
  }

  // Fallback to navigator.language
  const browserLang = navigator.language.split("-")[0].toLowerCase();
  if (browserLang === "fr") return "fr";
  if (browserLang === "es") return "es";

  // Default to English
  return "en";
};

// Helper function to get initial language
// Uses device language UNLESS user manually selected a language
const getInitialLanguage = (): SupportedLanguage => {
  // Check if user manually selected a language
  const manuallySelected =
    localStorage.getItem("languageManuallySelected") === "true";

  if (manuallySelected) {
    // User manually chose a language, use their preference
    const savedLanguage = localStorage.getItem("preferredLanguage");
    if (
      savedLanguage &&
      ["en", "fr", "es"].includes(savedLanguage as SupportedLanguage)
    ) {
      return savedLanguage as SupportedLanguage;
    }
  }

  // No manual selection, always use device language
  return getDeviceLanguage();
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(
    getInitialLanguage()
  );

  const setLanguage = (lang: SupportedLanguage) => {
    setLanguageState(lang);
    // Save user's manual selection
    localStorage.setItem("preferredLanguage", lang);
    // Mark that user manually selected this language
    localStorage.setItem("languageManuallySelected", "true");
  };

  // On mount, determine language based on manual selection or device language
  useEffect(() => {
    const initialLang = getInitialLanguage();
    setLanguageState(initialLang);
  }, []);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
